import { useEffect, useRef, useCallback } from 'react';
import type { RefObject } from 'react';
import type { Participant } from '../../types';
import { Badge } from '../common/Badge';
import { Avatar } from '../common/Avatar';
import { useMeetingStore } from '../../stores/meetingStore';
import { logWarning } from '../../services/logging/logger';

interface VideoTileProps {
  participant: Participant;
  isLocal?: boolean;
  isSpotlight?: boolean;
  onSpotlight?: () => void;
  enableAudio?: boolean;
}

/**
 * Single video tile for a participant.
 */
export function VideoTile({
  participant,
  isLocal = false,
  isSpotlight = false,
  onSpotlight,
  enableAudio = true,
}: VideoTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasVideo =
    participant.isCameraOn && isRenderableVideoTrack(participant.videoTrack, isLocal);
  const showAudio = enableAudio && !isLocal && !!participant.audioTrack;
  const cameraLabel = participant.isCameraOn ? 'Camera unavailable' : 'Camera off';

  useTrackElement(videoRef, hasVideo ? participant.videoTrack ?? null : null, hasVideo);
  useTrackElement(audioRef, showAudio ? participant.audioTrack ?? null : null, showAudio);

  const handleClick = useCallback(() => {
    // Delay single-click to allow double-click detection
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      return;
    }
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      onSpotlight?.();
    }, 250);
  }, [onSpotlight]);

  const handleDoubleClick = useCallback(() => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    openPopOutWindow(participant);
  }, [participant]);

  const tileClass = isSpotlight
    ? 'relative bg-black rounded-xl overflow-hidden border-2 border-primary group transition-all aspect-square h-[60vh] max-h-[500px]'
    : 'relative bg-surface rounded-xl overflow-hidden aspect-square border border-border group transition-all cursor-pointer hover:border-primary/50';

  return (
    <div
      className={tileClass}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      title="Click to spotlight, double-click to pop out"
    >
      <TileContent
        hasVideo={hasVideo}
        videoRef={videoRef}
        displayName={participant.displayName}
        avatarUrl={participant.avatarUrl}
        cameraLabel={cameraLabel}
        isLocal={isLocal}
      />
      <ParticipantBadges participant={participant} isLocal={isLocal} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
      <audio ref={audioRef} autoPlay playsInline muted={isLocal} className="hidden" />
    </div>
  );
}

/**
 * Renders the video element or fallback when video is unavailable.
 */
function TileContent({
  hasVideo,
  videoRef,
  displayName,
  avatarUrl,
  cameraLabel,
  isLocal,
}: {
  hasVideo: boolean;
  videoRef: RefObject<HTMLVideoElement>;
  displayName: string;
  avatarUrl?: string;
  cameraLabel: string;
  isLocal: boolean;
}) {
  if (hasVideo) {
    const videoClass = 'w-full h-full object-cover';
    return (
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        className={videoClass}
      />
    );
  }

  return (
    <VideoPlaceholder
      displayName={displayName}
      avatarUrl={avatarUrl}
      label={cameraLabel}
    />
  );
}

/**
 * Placeholder shown when no video stream is available.
 */
function VideoPlaceholder({
  displayName,
  avatarUrl,
  label,
}: {
  displayName: string;
  avatarUrl?: string;
  label: string;
}) {
  return (
    <div className="w-full h-full flex items-center justify-center bg-surface-hover">
      <div className="flex flex-col items-center gap-3">
        <Avatar name={displayName} imageUrl={avatarUrl} size="xl" />
        <span className="text-sm text-gray-400">{label}</span>
      </div>
    </div>
  );
}

/**
 * Displays participant metadata and status badges.
 */
function ParticipantBadges({
  participant,
  isLocal,
}: {
  participant: Participant;
  isLocal: boolean;
}) {
  return (
    <div className="absolute bottom-0 left-0 right-0 p-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium truncate text-white">
          {participant.displayName}
          {isLocal && ' (You)'}
        </span>

        {participant.isMuted && <MutedIcon />}
        {participant.isHost && <Badge variant="primary">Host</Badge>}
        {participant.isVR && <Badge variant="success">VR</Badge>}
      </div>
    </div>
  );
}

/**
 * Muted status icon for a participant.
 */
function MutedIcon() {
  return (
    <svg
      className="w-4 h-4 text-red-500"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Binds a media track to an HTML media element.
 */
function useTrackElement(
  ref: RefObject<HTMLMediaElement>,
  track: MediaStreamTrack | null,
  shouldPlay: boolean
): void {
  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    if (!track) {
      element.srcObject = null;
      return;
    }

    element.srcObject = new MediaStream([track]);
    if (shouldPlay) {
      void element.play().catch(() => undefined);
    }
  }, [ref, track, shouldPlay]);
}

/**
 * Determines whether a video track should be rendered.
 */
function isRenderableVideoTrack(track?: MediaStreamTrack, isLocal?: boolean): boolean {
  if (!track) {
    return false;
  }

  if (track.readyState !== 'live') {
    return false;
  }

  if (!track.enabled || track.muted) {
    return false;
  }

  if (isLocal && typeof track.getSettings === 'function') {
    const settings = track.getSettings();
    const width = settings.width ?? 0;
    const height = settings.height ?? 0;
    if (width === 0 || height === 0) {
      return false;
    }
  }

  return true;
}

/**
 * Opens a pop-out window for a participant's video.
 */
function openPopOutWindow(participant: Participant): void {
  const width = 600;
  const height = 600;
  const left = window.screenX + window.outerWidth - width - 20;
  const top = window.screenY + 100;

  const popup = window.open(
    '',
    `video-${participant.participantId}`,
    `width=${width},height=${height},left=${left},top=${top},resizable=yes`
  );

  if (!popup) {
    logWarning('Pop-out blocked by browser');
    return;
  }

  popup.document.title = `${participant.displayName} - Video`;
  popup.document.body.style.cssText =
    'margin:0;padding:0;background:#000;overflow:hidden;';

  const video = popup.document.createElement('video');
  video.autoplay = true;
  video.playsInline = true;
  video.muted = true;
  video.style.cssText = 'width:100%;height:100%;object-fit:cover;';

  if (participant.videoTrack) {
    video.srcObject = new MediaStream([participant.videoTrack]);
    void video.play().catch(() => undefined);
  }

  popup.document.body.appendChild(video);

  // Update video when track changes
  const unsubscribe = useMeetingStore.subscribe((state) => {
    const updated =
      state.participants.find((p) => p.participantId === participant.participantId) ??
      (state.localParticipant?.participantId === participant.participantId
        ? state.localParticipant
        : null);

    if (updated?.videoTrack) {
      video.srcObject = new MediaStream([updated.videoTrack]);
      void video.play().catch(() => undefined);
    } else {
      video.srcObject = null;
    }
  });

  popup.addEventListener('beforeunload', () => {
    unsubscribe();
  });
}
